import { faCog } from '@fortawesome/pro-solid-svg-icons/faCog';
import { ActionDetails, useMailActions } from '@ukpostbox/client-data';
import {
	Button,
	Colour,
	ColourStateSolid,
	Modal,
	ModalType,
	useNotifications,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { GetArrayIndexByKeyValue, StripHtml } from '@ukpostbox/helpers';
import { useEffect, useRef, useState } from 'react';

interface ConfirmActionModalType extends ModalType {
	folderSlug: string;
	mailIds: Array<number>;
	actionId: number;
	actionMailItemsCallback: (id: number, name: string, callback: () => void) => void;
	actionMailItemsUiCallback: (name: string) => void;
	undoActionMailItemsCallback: () => void;
}

function ConfirmActionModal(props: ConfirmActionModalType) {
	const { t } = useTranslation();
	const { AddToast } = useNotifications();

	const [selectedActionDetails, setSelectedActionDetails] = useState<ActionDetails | null>(null);
	const [actionActive, setActionActive] = useState<boolean>(false);

	const [timerComplete, setTimerComplete] = useState<boolean>(props.mailIds.length <= 8);

	const mailActions: any = useMailActions();

	const ActionComplete = () => {
		setActionActive(false);
		props.actionedCallback();
	};

	const ActionItemReset = () => {
		props.undoActionMailItemsCallback();
		ActionComplete();
	};

	const ActionItems = () => {
		setActionActive(true);

		props.closeCallback();

		props.actionMailItemsUiCallback(selectedActionDetails?.name as string);

		AddToast({
			content: t('mailItem.messages.actionRequested'),
			colour: Colour.Warning,
			icon: faCog,
			iconSpins: true,
			actionLabel: 'Undo',
			actionCallback: () => ActionItemReset(),
			onDismissedCallback: () =>
				props.actionMailItemsCallback(props.actionId, selectedActionDetails?.name as string, () =>
					ActionComplete(),
				),
		});
	};

	const GetSelectedActionDetails = () => {
		const actionIndex = GetArrayIndexByKeyValue(mailActions.data.data, 'transitionId', props.actionId);
		setSelectedActionDetails(mailActions.data.data[actionIndex]);
	};

	useEffect(() => {
		if (mailActions?.data?.data && props.actionId !== -1) {
			GetSelectedActionDetails();
		}
	}, [mailActions.data, props]);

	const progressRef = useRef<HTMLDivElement>(null);

	const hideTimer = useRef<any>(null);

	const initialising = useRef<boolean>(false);
	const waitTime = 5000;

	const UpdateProgress = (value: number) => {
		if (progressRef.current) {
			progressRef.current.style.width = `${value}%`;
		}
	};

	const Initialise = () => {
		initialising.current = true;

		if (hideTimer.current) {
			clearTimeout(hideTimer.current);
		}

		if (progressRef.current) {
			progressRef.current.style.transitionDuration = `${waitTime}ms`;
		}

		setTimeout(() => {
			UpdateProgress(100);
		}, 100);

		setTimeout(() => {
			setTimerComplete(true);
		}, waitTime + 100);

		initialising.current = false;
	};

	useEffect(() => {
		if (progressRef.current) {
			Initialise();
		}
	}, []);

	return (
		<Modal
			title={`${selectedActionDetails?.name} ${props.mailIds.length} ${
				props.mailIds.length === 1 ? 'Item' : 'Items'
			}`}
			isOpen={props.isOpen}
			closeCallback={() => props.closeCallback()}
			actionedCallback={() => props.actionedCallback()}
		>
			<p>
				{selectedActionDetails?.prompt
					? StripHtml(selectedActionDetails?.prompt as string)
					: selectedActionDetails?.hint}
			</p>

			{props.mailIds.length > 8 && (
				<div style={{ margin: '0 auto 20px', textAlign: 'center' }}>
					<div className='progress slim'>
						<div
							ref={progressRef}
							className='progress-inner alert'
							style={{ width: 0, transition: `width ${waitTime}ms`, transitionTimingFunction: 'linear' }}
						/>
					</div>
				</div>
			)}

			<Button
				title='Confirm'
				disabled={!timerComplete || actionActive}
				loading={actionActive}
				fullWidth
				colour={ColourStateSolid.Primary}
				onClick={() => ActionItems()}
			/>
		</Modal>
	);
}

export default ConfirmActionModal;
